import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, PencilSimple, User, Briefcase, Users as UsersIcon, Phone,
  IdentificationCard, Cake, CalendarBlank, Paperclip, Info, Note,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmployeeDialog from "@/components/workforce/EmployeeDialog";
import { workforceApi, formatDate, formatBirthday } from "@/components/workforce/workforceApi";

/**
 * WF-101 · Employee Profile Workspace
 *
 * Single management location for every field on one employee, plus
 * read-only calculated fields (Years of Service, Anniversary, Active,
 * and a Vacation Tier slot awaiting the Vacation Management Engine).
 *
 * Attachments are architecture-only per PO 2026-02-04 — the collection
 * exists on the backend but no upload endpoint is wired.
 */
export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "Owner";
  const [profile, setProfile] = useState(null);
  const [workGroups, setWorkGroups] = useState([]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: meta }] = await Promise.all([
        workforceApi.employeeProfile(id),
        workforceApi.metadata(),
      ]);
      setProfile(data);
      setWorkGroups(meta.work_groups || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load employee profile");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface p-6" data-testid="employee-profile-error">
        <button
          onClick={() => navigate("/workforce")}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Back to Workforce
        </button>
        <div className="mt-6 rounded-md border border-status-error-bg bg-status-error-bg p-4 text-sm text-status-error-fg">
          {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return <div className="p-6 text-sm text-slate-500">Loading profile…</div>;
  }

  const { employee: emp, calculated, attachments } = profile;
  const initials = (emp.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const yearsLabel = calculated.years_of_service === null
    ? "—"
    : calculated.years_of_service === 1 ? "1 year" : `${calculated.years_of_service} years`;

  return (
    <div className="min-h-screen bg-surface p-6 md:p-8" data-testid="employee-profile-page">
      <div className="mb-4">
        <button
          data-testid="employee-profile-back"
          onClick={() => navigate("/workforce")}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Back to Workforce
        </button>
      </div>

      <PageHeader
        title={emp.full_name}
        description={[emp.role_title, emp.department].filter(Boolean).join(" · ") || "Employee"}
        actions={!readOnly && (
          <button
            data-testid="employee-profile-edit"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900"
          >
            <PencilSimple size={13} weight="bold" /> Edit Employee
          </button>
        )}
      />

      {/* Identity banner */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div
          data-testid="employee-profile-avatar"
          className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 font-display text-xl font-semibold text-white"
        >
          {initials}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Employee #{emp.employee_number ?? "—"}</div>
          <div className="text-lg font-semibold text-slate-900">{emp.full_name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
            <StatusPill active={calculated.active} status={emp.status} />
            {emp.work_group && (
              <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 font-semibold text-sky-800">
                {emp.work_group}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Personal Information" icon={<User size={16} />}>
          <Field label="Full Name" value={emp.full_name} />
          <Field label="Preferred Name" value={emp.preferred_name} testid="employee-profile-preferred-name" />
          <Field label="Birthday" value={formatBirthday(emp.birthday_mm_dd)} />
          <Field label="Address" value={emp.address} />
        </Section>

        <Section title="Employment" icon={<Briefcase size={16} />}>
          <Field label="Position" value={emp.role_title} />
          <Field label="Department" value={emp.department} />
          <Field label="Employee Number" value={emp.employee_number} />
          <Field label="Hire Date" value={formatDate(emp.hire_date)} />
          <Field label="Status" value={emp.status} />
        </Section>

        <Section title="Work Group" icon={<UsersIcon size={16} />}>
          <Field label="Work Group" value={emp.work_group} />
          <Field label="Manager" value={<span className="text-slate-400">Coming soon</span>} />
        </Section>

        <Section title="Contact" icon={<Phone size={16} />}>
          <Field label="Company Email" value={emp.email} />
          <Field label="Personal Email" value={emp.personal_email} />
          <Field label="Phone Number" value={emp.phone_number} />
        </Section>

        <Section title="Emergency Contact" icon={<IdentificationCard size={16} />}>
          <Field label="Name" value={emp.emergency_contact_name} />
          <Field label="Phone" value={emp.emergency_contact_phone} />
          <Field label="Relationship" value={emp.emergency_contact_relationship} />
        </Section>

        <Section title="Milestones" icon={<CalendarBlank size={16} />}>
          <Field
            label="Years of Service"
            testid="employee-profile-years-of-service"
            value={yearsLabel}
          />
          <Field
            label="Employment Anniversary"
            testid="employee-profile-anniversary"
            value={calculated.employment_anniversary
              ? `${formatDate(calculated.employment_anniversary.next_date)} · in ${calculated.employment_anniversary.days_until} days`
              : "—"}
          />
          <Field
            label="Next Birthday"
            testid="employee-profile-next-birthday"
            value={calculated.next_birthday
              ? `${formatDate(calculated.next_birthday.next_date)} · in ${calculated.next_birthday.days_until} days`
              : "—"}
          />
        </Section>

        <Section title="Vacation" icon={<Cake size={16} />}>
          <Field
            label="Current Vacation Tier"
            testid="employee-profile-vacation-tier"
            value={<span className="text-slate-400">—</span>}
          />
          <div className="col-span-full mt-1 flex items-center gap-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-500">
            <Info size={12} /> {calculated.current_vacation_tier_caption || "Awaiting Vacation Management Engine"}
          </div>
        </Section>

        <Section title="Notes" icon={<Note size={16} />} full>
          {emp.notes
            ? <p className="whitespace-pre-wrap text-sm text-slate-700">{emp.notes}</p>
            : <p className="text-sm text-slate-400">No notes on file.</p>}
        </Section>

        <Section title="Attachments" icon={<Paperclip size={16} />} full>
          {attachments.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500" data-testid="employee-profile-attachments-placeholder">
              Attachments architecture is in place.
              <div className="mt-1 text-[11px]">Upload storage will be enabled in a future release.</div>
            </div>
          )}
          {attachments.length > 0 && (
            <ul className="space-y-1 text-sm">
              {attachments.map((a) => (
                <li key={a.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <span>{a.filename}</span>
                  <span className="text-[11px] text-slate-500">{formatDate(a.uploaded_at?.slice(0, 10))}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <EmployeeDialog
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); }}
        editing={emp}
        workGroups={workGroups}
        readOnly={readOnly}
      />
    </div>
  );
}

function Section({ title, icon, children, full }) {
  return (
    <section
      data-testid={`employee-profile-section-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm ${full ? "lg:col-span-3" : ""}`}
    >
      <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wider text-slate-500">
        {icon} {title}
      </h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
        {children}
      </div>
    </section>
  );
}

function Field({ label, value, testid }) {
  const shown = value === null || value === undefined || value === "" ? "—" : value;
  return (
    <div data-testid={testid} className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-0.5 truncate text-sm text-slate-900">{shown}</div>
    </div>
  );
}

function StatusPill({ active, status }) {
  return (
    <span className={`rounded-full border px-2 py-0.5 font-semibold ${
      active
        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
        : "border-slate-200 bg-slate-100 text-slate-600"
    }`}>
      {status || (active ? "Active" : "Inactive")}
    </span>
  );
}
